/**
 * Create Agreement Dialog - Form to create new premium partner agreement
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { premiumPartnerService, CreateAgreementData } from '@/services/premiumPartnerService';
import toast from 'react-hot-toast';

interface CreateAgreementDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAgreementDialog: React.FC<CreateAgreementDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateAgreementData>({
    hostId: '',
    platformFeePercentage: 6.0,
    bookingFeePercentage: 2.5,
    minimumMonthlyBookings: 100,
    minimumMonthlyRevenue: 5000000,
    minimumVehicleCount: 50,
    fuelResponsibility: 'partner',
    driverProvision: 'partner',
    insuranceResponsibility: 'partner',
    startDate: '',
    endDate: '',
    autoRenewal: false,
    internalNotes: '',
    createdBy: localStorage.getItem('userId') || ''
  });

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await premiumPartnerService.createAgreement(formData);

      if (response.success) {
        toast.success('Premium partner agreement created successfully!');
        onSuccess();
      } else {
        toast.error(response.error || 'Failed to create agreement');
      }
    } catch (error) {
      console.error('Error creating agreement:', error);
      toast.error('An error occurred while creating the agreement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create Premium Partner Agreement</DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Create a custom agreement for high-value fleet partners. Approving the agreement will upgrade the host to Premium tier.
            </Alert>

            <Grid container spacing={3}>
              {/* Host ID */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Host User ID"
                  required
                  value={formData.hostId}
                  onChange={(e) => setFormData({ ...formData, hostId: e.target.value })}
                  placeholder="Enter host's user UUID"
                  helperText="The UUID of the host account to upgrade"
                />
              </Grid>

              {/* Commission Structure */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Commission Structure
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Platform Fee (%)"
                  type="number"
                  required
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={formData.platformFeePercentage}
                  onChange={(e) => setFormData({ ...formData, platformFeePercentage: parseFloat(e.target.value) })}
                  helperText={`Host keeps ${(100 - formData.platformFeePercentage).toFixed(1)}%`}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Booking Fee (%)"
                  type="number"
                  required
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={formData.bookingFeePercentage}
                  onChange={(e) => setFormData({ ...formData, bookingFeePercentage: parseFloat(e.target.value) })}
                  helperText="Added to renter total"
                />
              </Grid>

              {/* Minimum Requirements */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                  Minimum Requirements
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Monthly Bookings"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.minimumMonthlyBookings}
                  onChange={(e) => setFormData({ ...formData, minimumMonthlyBookings: parseInt(e.target.value) })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Monthly Revenue (₦)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.minimumMonthlyRevenue}
                  onChange={(e) => setFormData({ ...formData, minimumMonthlyRevenue: parseInt(e.target.value) })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Minimum Vehicles"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.minimumVehicleCount}
                  onChange={(e) => setFormData({ ...formData, minimumVehicleCount: parseInt(e.target.value) })}
                />
              </Grid>

              {/* Contract Period */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                  Contract Period
                </Typography>
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="End Date (Optional)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.autoRenewal}
                      onChange={(e) => setFormData({ ...formData, autoRenewal: e.target.checked })}
                    />
                  }
                  label="Auto-renew"
                />
              </Grid>

              {/* Internal Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Internal Notes"
                  multiline
                  rows={3}
                  value={formData.internalNotes}
                  onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                  placeholder="Internal admin notes about this agreement..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Agreement'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
